<template>
  <b-modal
    :title="$t('withdraw')"
    hide-footer
    id="withdraw-modal"
    ref="withdraw-modal"
    @hide="errors = {}"
  >
    <div data-cy="withdrawal-request-modal">
      <b-form-group>
        <b-form-label
          slot="label"
          :label="'* ' + $t('amount')"
          for="withdraw-amount"
          :errors="errors.amount"
          data-cy="amount-label"
        />
        <b-input-group prepend="$">
          <b-input-number
            v-model="amount"
            id="withdraw-amount"
            type="text"
            size="lg"
            required
          />
        </b-input-group>

        <span class="text-success font-weight-bolder" style="font-size: .85em">
          {{ $t('balance') }}:
          <span data-cy="current-balance">{{ balance }}</span>
        </span>
      </b-form-group>
      <b-form-group v-if="walletTypesList">
        <b-form-label
          slot="label"
          :label="'* ' + $t('walletType')"
          for="withdraw-wallet-type"
        />
        <b-form-select
          v-model="form.walletType"
          :options="walletTypesList"
          id="withdraw-wallet-type"
        />
      </b-form-group>
      <b-form-group>
        <b-form-label
          slot="label"
          :label="$tc('wallet', 1)"
          for="withdraw-wallet"
          :errors="errors.wallet"
        />
        <b-form-input
          v-model="walletNumber"
          id="withdraw-wallet"
          type="text"
          size="lg"
        />
      </b-form-group>
      <b-form-group
        v-if="canUpdate"
        :label="'' + $t('wantUpdateWallet') + ''"
        data-cy="update-wallet"
      >
        <b-form-radio-group
          id="update-profile-group"
          v-model="wantUpdate"
          name="update-profile-group"
        >
          <b-form-radio :value="true" data-cy="update-true">
            {{ $t('yes') }}
          </b-form-radio>
          <b-form-radio :value="false" data-cy="update-false">
            {{ $t('no') }}
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>
      <b-form-group>
        <b-form-label
          slot="label"
          :label="$t('leaveComment')"
          for="withdraw-comment"
        />
        <b-form-textarea id="withdraw-comment" v-model="comment" />
      </b-form-group>
      <div class="text-right">
        <button
          @click="submit()"
          class="btn btn-primary btn-success btn-sm"
          data-cy="submit-withdraw-modal"
        >
          {{ $t('send') }}
        </button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import cloneDeep from 'clone';
import merge from 'merge';

export default {
  props: {
    balance: {
      required: true,
      type: Number
    }
  },

  data() {
    return {
      form: {
        wallet: null,
        walletType: null
      },
      amount: null,
      comment: null,
      walletNumber: null,
      walletOriginalNumber: null,
      errors: {},
      amountRequired: null,
      canUpdate: false,
      wantUpdate: false,
      availableWalletTypes: {
        paxum: null,
        webmoney: null,
        yandexMoney: null,
        epayments: null,
        phone: null
      },
      walletTypesList: []
    };
  },

  computed: {
    user() {
      return this.$store.getters.user;
    },
    userWallets() {
      return this.$store.getters.user.wallets;
    }
  },

  watch: {
    form: {
      handler(obj) {
        if (obj.walletType !== 'phone') {
          const number = !obj.walletType
            ? null
            : this.userWallets[obj.walletType];
          this.walletNumber = number;
          this.walletOriginalNumber = number;
        } else {
          this.walletNumber = this.user.phone;
          this.walletOriginalNumber = this.user.phone;
        }
      },
      deep: true
    },
    walletNumber(number) {
      this.canUpdate = number !== this.walletOriginalNumber;
    },
    walletTypesList() {
      this.form.walletType = this.walletTypesList[0].value;
    },
    canUpdate(val) {
      if (val === false) this.wantUpdate = false;
    }
  },

  methods: {
    processWalletsList() {
      const wallets = merge(
        cloneDeep(this.userWallets),
        this.availableWalletTypes
      );
      wallets.phone = this.user.phone ? this.user.phone : null;

      const walletsList = Object.keys(this.availableWalletTypes).map(wallet => {
        return {
          value: wallet,
          text: wallet
        };
      });

      this.walletTypesList = walletsList;
    },
    submit() {
      this.errors = {};
      const request = cloneDeep(this.form);
      request.amount = this.amount;
      request.comment = this.comment;
      request.wallet = this.walletNumber;
      request.walletType = this.form.walletType;
      request.userId = this.$store.getters.user.id;

      this.$api.bounty.post('/v0/withdrawal-requests', request).then(
        () => {
          this.form.amount = null;
          this.errors = {};
          this.resetData();
          this.$refs['withdraw-modal'].hide();
          this.$emit('update-requests-list');
          this.$notify('alert', {
            text: this.$t('withdrawalRequestSsended'),
            variant: 'success',
            time: 5000
          });
        },
        e => {
          this.errors = {};

          if (e.response.data.errors) {
            this.errors = this.$parseErrors(e.response.data.errors) || {};
          } else {
            e.response.data.detail &&
              this.$notify('alert', {
                text: e.response.data.detail,
                variant: 'danger',
                time: 0
              });
          }
        }
      );

      if (this.wantUpdate) this.updateUserProfile();
    },

    updateUserProfile() {
      const user = cloneDeep(this.user);

      if (this.form.walletType === 'phone') {
        user.phone = this.walletNumber;
      } else {
        user.wallets[this.form.walletType] = this.walletNumber;
      }

      this.$api.auth.put(`/profile`, user).then(
        () => {
          this.errors = {};
          this.$notify('alert', {
            text: this.$t('profileUpdated'),
            variant: 'success',
            time: 5000
          });
          this.$store.dispatch('fetchUser').then(() => {
            this.resetData();
          });
        },
        e => {
          this.errors = {};

          if (e.response.data.error.errors) {
            this.errors = e.response.data.error
              ? this.$parseAuthErrors(e.response.data.error.errors)
              : {};
          } else {
            e.response.data.error.message &&
              this.$notify('alert', {
                text: e.response.data.error.message,
                variant: 'danger',
                time: 5000
              });
          }
        }
      );
    },

    resetData() {
      this.form = {
        wallet: null,
        walletType: null
      };
      this.amount = null;
      this.comment = null;
      this.walletNumber = null;
    }
  },

  created() {
    this.processWalletsList();
  }
};
</script>
